import React, { useState, useEffect, useCallback } from 'react';
import { Stage, Layer, Rect, Text } from 'react-konva';
import Grid from '../../components/Grid';
import { jwtDecode } from 'jwt-decode';
import { capitalize } from '../../utils/StringUtils.jsx';
import { format, startOfDay, endOfDay, isWithinInterval } from 'date-fns';
import { useSSE } from '../../Settings/SSEProvider.jsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import axiosInstance from "../../api/axiosConfig.jsx";

const ViewTafelOpstelling = () => {
    const { reservations } = useSSE();
    const [tables, setTables] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState('');
    const [tableAssignments, setTableAssignments] = useState(new Map());
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [selectedTableId, setSelectedTableId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [scale, setScale] = useState(1);
    const gridSize = 35;
    const rows = 15;
    const columns = 25;
    const width = gridSize * columns;
    const height = gridSize * rows;

    const updateScale = () => {
        const containerWidth = window.innerWidth;  // Volledige breedte van het scherm
        const containerHeight = window.innerHeight * 0.71; // Beperk de hoogte
        const scaleWidth = containerWidth / width;
        const scaleHeight = containerHeight / height;
        const newScale = Math.min(scaleWidth, scaleHeight);
        setScale(newScale);
    };

    useEffect(() => {
        updateScale();
        window.addEventListener('resize', updateScale);
        return () => window.removeEventListener('resize', updateScale);
    }, []);

    const filterTodayReservations = useCallback((allReservations) => {
        const today = new Date();
        const startOfToday = startOfDay(today);
        const endOfToday = endOfDay(today);

        return allReservations.filter(reservation =>
            isWithinInterval(new Date(reservation.start), { start: startOfToday, end: endOfToday })
        );
    }, []);

    useEffect(() => {
        const fetchTablesAndRooms = async () => {
            const authToken = sessionStorage.getItem('authToken');
            const decodedToken = jwtDecode(authToken);
            const companyName = decodedToken.companyName;

            try {
                const response = await axiosInstance.get(`/tables/company/${companyName}`);
                const fetchedTables = response.data;
                setTables(fetchedTables);
                const uniqueRooms = [...new Set(fetchedTables.map(table => capitalize(table.room)))];
                setRooms(uniqueRooms);
                if (uniqueRooms.length > 0) {
                    setSelectedRoom(uniqueRooms[0]);
                }
            } catch (error) {
                console.error('Error fetching table data:', error);
            }
        };

        fetchTablesAndRooms();
    }, []);

    useEffect(() => {
        if (reservations.length > 0) {
            const todayReservations = filterTodayReservations(reservations);
            handleTableUpdates(todayReservations);
            handleAssignTables(todayReservations);
        }
    }, [reservations, filterTodayReservations]);

    const handleTableUpdates = (todayReservations) => {
        const reservedTableIds = new Set(todayReservations.flatMap(reservation => reservation.tableIds || []));
        setTables(prevTables => prevTables.map(table => ({
            ...table,
            reserved: reservedTableIds.has(table.tableId),
        })));

        setTableAssignments(prevAssignments => {
            const updatedAssignments = new Map();
            todayReservations.forEach(reservation => {
                (reservation.tableIds || []).forEach(tableId => {
                    updatedAssignments.set(tableId, reservation.id);
                });
            });

            return updatedAssignments;
        });
    };

    const handleAssignTables = async (todayReservations) => {
        const payload = todayReservations.map(reservation => ({
            data: reservation.data
        }));

        try {
            const response = await axiosInstance.post('/table-assignment/assign', {
                reservations: payload,
                tables: tables
            });

            const assignments = response.data.assignments;

            if (typeof assignments === 'object' && assignments !== null) {
                setTableAssignments(prevAssignments => {
                    const updatedAssignments = new Map(prevAssignments);

                    Object.entries(assignments).forEach(([tableId, reservationId]) => {
                        updatedAssignments.set(parseInt(tableId), reservationId);
                    });

                    setTables(prevTables => prevTables.map(table =>
                        updatedAssignments.has(table.tableId)
                            ? { ...table, reserved: true }
                            : { ...table, reserved: false }
                    ));

                    return updatedAssignments;
                });
            }
        } catch (error) {
            console.error('Fout bij het toewijzen van tafels:', error);
        }
    };

    const openModal = (reservation) => {
        setSelectedReservation(reservation);
        setModalOpen(true);
    };

    const closeModal = () => {
        setSelectedReservation(null);
        setModalOpen(false);
    };

    const handleDeleteReservation = async () => {
        if (selectedReservation) {
            try {
                await axiosInstance.delete(`/reservations/${selectedReservation.id}`);
                setTableAssignments(prevAssignments => {
                    const updatedAssignments = new Map(prevAssignments);
                    updatedAssignments.forEach((reservationId, tableId) => {
                        if (reservationId === selectedReservation.id) {
                            updatedAssignments.delete(tableId);
                        }
                    });
                    return updatedAssignments;
                });
                setTables(prevTables => prevTables.map(table =>
                    tableAssignments.has(table.tableId) && tableAssignments.get(table.tableId) === selectedReservation.id
                        ? { ...table, reserved: false }
                        : table
                ));
                closeModal();
            } catch (error) {
                console.error('Error deleting reservation:', error);
            }
        }
    };

    const getTableNumbersForReservation = (reservationId) => {
        return Array.from(tableAssignments.entries())
            .filter(([_, resId]) => resId === reservationId)
            .map(([tableId]) => {
                const table = tables.find(t => t.tableId === tableId);
                return table ? table.tableNumber : 'Onbekend';
            })
            .join(', ');
    };

    return (
        <div className="container-fluid p-3" style={{ paddingTop: '70px' }}>
            {/* Room Selector */}
            <div className="row">
                <div className="col-10 col-sm-10 col-lg-8 mb-4">
                    <select
                        value={selectedRoom}
                        onChange={(e) => setSelectedRoom(e.target.value)}
                        className="form-select mb-3"
                    >
                        {rooms.map((room, index) => (
                            <option key={index} value={room}>{room}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="row" >
                {/* Table Layout */}
                <div className="col-10 col-sm-10 col-lg-8 mb-4">
                    <div className="card">
                        <div className="card-body p-2">
                            <div
                                id="stage-container"
                                className="border rounded"
                                style={{
                                    width: '100%',
                                    overflowX: 'auto',
                                    overflowY: 'auto',
                                    maxWidth: '100vw',
                                    marginLeft: '0',  // Verwijder alle marge aan de linkerzijde
                                    paddingLeft: '0', // Geen extra padding aan de linkerzijde
                                }}
                            >
                                <Stage
                                    width={width * scale}
                                    height={height * scale}
                                    scaleX={scale}
                                    scaleY={scale}
                                >
                                    <Layer>
                                        <Grid gridSize={gridSize} width={width} height={height} color="#ddd"/>
                                        {tables
                                            .filter(table => capitalize(table.room) === selectedRoom)
                                            .map((table) => (
                                                <React.Fragment key={table.tableId}>
                                                    <Rect
                                                        x={table.x}
                                                        y={table.y}
                                                        width={gridSize}
                                                        height={gridSize}
                                                        fill={tableAssignments.has(table.tableId)
                                                            ? 'red'
                                                            : (selectedTableId === table.tableId ? 'blue' : 'green')}
                                                        stroke="black"
                                                        strokeWidth={1}
                                                        onClick={() => setSelectedTableId(table.tableId)}
                                                    />
                                                    <Text
                                                        x={table.x}
                                                        y={table.y}
                                                        text={`Tafel ${table.tableNumber}`}
                                                        fontSize={14}
                                                        fontFamily="Arial"
                                                        fill="black"
                                                        offsetX={gridSize / 2}
                                                        offsetY={gridSize / 2}
                                                    />
                                                </React.Fragment>
                                            ))}
                                    </Layer>
                                </Stage>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reservation List */}
                <div className="col-10 col-sm-3 col-lg-4 mb-4">
                    <div className="card">
                        <div className="card-header">
                            <h5>Reserveringen voor vandaag</h5>
                        </div>
                        <div className="card-body p-0" style={{maxHeight: '60vh', overflowY: 'auto'}}>
                            {filterTodayReservations(reservations).length > 0 ? (
                                <div className="list-group list-group-flush">
                                    {filterTodayReservations(reservations).map(reservation => (
                                        <button
                                            key={reservation.id}
                                            className={`list-group-item list-group-item-action ${
                                                selectedReservation?.id === reservation.id ? 'active' : ''
                                            }`}
                                            onClick={() => openModal(reservation)}
                                        >
                                            <div className="d-flex w-100 justify-content-between">
                                                <h6 className="mb-1">{reservation.title}</h6>
                                                <small>{new Date(reservation.start).toLocaleTimeString()}</small>
                                            </div>
                                            <p className="mb-1">
                                                <small className="text-muted">
                                                    Tafel(s): {getTableNumbersForReservation(reservation.id)}
                                                </small>
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-3 text-center text-muted">
                                    Geen reserveringen voor vandaag.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal remains the same */}
            {modalOpen && selectedReservation && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Reserveringsdetails</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={closeModal}
                                        aria-label="Close"
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <strong>Titel:</strong> {selectedReservation.title}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Starttijd:</strong> {new Date(selectedReservation.start).toLocaleTimeString()}
                                    </div>
                                    <div>
                                        <strong>Tafelnummers:</strong> {getTableNumbersForReservation(selectedReservation.id)}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={handleDeleteReservation}
                                    >
                                        Reservering Verwijderen
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModal}
                                    >
                                        Sluiten
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );

};

export default ViewTafelOpstelling;
